import { CheckCircle2, Ban, Lock, FileCheck2, Receipt, Phone, ArrowUpRight } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F2EDE2";
const PAPER = "#FFFEFB";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FONT_MONO = "ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, monospace";
const PROMISES = [{
  icon: Ban,
  title: "No callout fee, ever",
  body: "We don't charge you for showing up or for the on-site inspection. If you don't approve the quote, you walk away at zero cost."
}, {
  icon: Lock,
  title: "Fixed price, locked in writing",
  body: "Your quote is locked. Same price even if the job takes us longer than expected — that's our problem to absorb, not yours."
}, {
  icon: FileCheck2,
  title: "GST included. No hidden extras",
  body: "GST built into the quoted figure. No travel surcharges, no 'parts markup' added later, no weekend premium without it being agreed upfront."
}, {
  icon: CheckCircle2,
  title: "You approve before any work begins",
  body: "Quote on paper or SMS, with your signature or 'OK reply' on file. No tool comes out until you've explicitly said yes."
}];
const RECEIPT_LINES = [{
  label: "Torsion spring replacement",
  value: "FIXED"
}, {
  label: "Cable inspection & adjust",
  value: "FIXED"
}, {
  label: "Track realignment",
  value: "FIXED"
}, {
  label: "Lubrication & 10-point check",
  value: "INCLUDED"
}];
const RECEIPT_FEES = [{
  label: "Callout fee",
  value: "$0.00"
}, {
  label: "Travel surcharge",
  value: "$0.00"
}, {
  label: "Hourly labour",
  value: "NEVER"
}, {
  label: "GST",
  value: "INCLUDED"
}];
export default function PricingTransparency() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="pricing-transparency-heading">
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Decorative gold blob bottom-left */}
      <div aria-hidden className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full pointer-events-none opacity-[0.05]" style={{
      backgroundColor: GOLD,
      filter: "blur(80px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* === Top strip === */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-16 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD_DARK}66`,
            backgroundColor: `${GOLD}1A`
          }}>
              <Receipt className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD_DARK
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD_DARK
          }}>
              Pricing Transparency
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${NAVY}26`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED
        }}>
            What you see · What you pay
          </div>
        </div>

        {/* === H2 === */}
        <div className="mb-14 md:mb-20 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <h2 id="pricing-transparency-heading" className="lg:col-span-9 font-extrabold" style={{
          fontFamily: FONT_HEAD,
          color: NAVY,
          fontSize: "clamp(2rem, 5vw, 4rem)",
          lineHeight: 1.02,
          letterSpacing: "-0.03em"
        }}>
            What you see on day one
            <br className="hidden md:block" />
            is{" "}
            <span style={{
            color: GOLD_DARK
          }}>
              exactly what you pay
            </span>{" "}
            on day done.
          </h2>
          <p className="lg:col-span-3 text-[14px] leading-relaxed" style={{
          color: INK_MUTED
        }}>
            Four pricing pledges, locked in writing on every job.
          </p>
        </div>

        {/* === Asymmetric grid === */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* === LEFT — Promises list === */}
          <div className="lg:col-span-5">
            <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-7" style={{
            color: INK_MUTED
          }}>
              — Our pricing pledge
            </div>

            <ul className="space-y-7">
              {PROMISES.map((promise, idx) => <Promise key={promise.title} promise={promise} num={String(idx + 1).padStart(2, "0")} />)}
            </ul>

            {/* Bottom signature line */}
            <div className="mt-9 pt-6 border-t flex items-start gap-3" style={{
            borderColor: `${NAVY}1A`
          }}>
              <span className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full" style={{
              backgroundColor: NAVY,
              color: GOLD
            }}>
                <Lock className="h-3.5 w-3.5" strokeWidth={2.5} />
              </span>
              <p className="text-[13px] italic leading-snug font-medium" style={{
              color: INK,
              fontFamily: FONT_HEAD
            }}>
                &ldquo;If we miss a part on the quote, that&rsquo;s on us &mdash;
                the price you signed is the price you pay.&rdquo;
              </p>
            </div>
          </div>

          {/* === RIGHT — Sample quote receipt card === */}
          <div className="lg:col-span-7">
            <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-7 text-right md:text-left" style={{
            color: INK_MUTED
          }}>
              — Anatomy of a Gold Garage Door quote
            </div>

            <ReceiptCard />
          </div>
        </div>

        {/* === Bottom CTA strip === */}
        <div className="mt-16 md:mt-20">
          <div className="relative rounded-2xl border overflow-hidden p-6 md:p-8 flex items-center justify-between gap-6 flex-wrap" style={{
          backgroundColor: NAVY,
          borderColor: `${GOLD}33`
        }}>
            {/* Top gold accent */}
            <div aria-hidden className="absolute top-0 left-8 right-8 h-[2px]" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}80 50%, transparent 100%)`
          }} />

            <div className="flex-1 min-w-[260px]">
              <div className="text-[10px] font-bold tracking-[0.28em] uppercase mb-1.5" style={{
              color: GOLD
            }}>
                Want a real fixed-price quote?
              </div>
              <h3 className="font-bold text-white" style={{
              fontFamily: FONT_HEAD,
              fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
              lineHeight: 1.2,
              letterSpacing: "-0.015em"
            }}>
                We&rsquo;ll have one on your phone within the hour.
              </h3>
            </div>

            <a href="tel:0485000933" className="group inline-flex items-center gap-2.5 rounded-full px-6 py-3 text-[14px] font-bold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]" style={{
            backgroundColor: GOLD,
            color: NAVY,
            fontFamily: FONT_BODY,
            boxShadow: `0 14px 40px -10px ${GOLD}99`
          }}>
              <Phone className="h-4 w-4" strokeWidth={2.5} />
              0485 000 933
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </div>
    </section>;
}
function Promise({
  promise,
  num
}: {
  promise: {
    icon: React.ComponentType<{
      className?: string;
      strokeWidth?: number;
    }>;
    title: string;
    body: string;
  };
  num: string;
}) {
  const Icon = promise.icon;
  return <li className="flex items-start gap-4 group">
      <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl border transition-all duration-300 group-hover:scale-105" style={{
      backgroundColor: PAPER,
      borderColor: `${GOLD_DARK}33`
    }}>
        <Icon className="h-5 w-5" strokeWidth={2} style={{
        color: GOLD_DARK
      } as React.CSSProperties} />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{
          color: GOLD_DARK
        }}>
            Pledge {num}
          </span>
          <span className="h-px w-4" style={{
          backgroundColor: `${INK_MUTED}40`
        }} />
        </div>
        <h3 className="font-bold mb-1.5" style={{
        fontFamily: FONT_HEAD,
        color: NAVY,
        fontSize: "1.0625rem",
        lineHeight: 1.25,
        letterSpacing: "-0.012em"
      }}>
          {promise.title}
        </h3>
        <p className="text-[14px] leading-[1.6]" style={{
        color: INK_MUTED
      }}>
          {promise.body}
        </p>
      </div>
    </li>;
}
function ReceiptCard() {
  return <div className="relative max-w-[560px]" style={{
    transform: "rotate(-0.4deg)"
  }}>
      {/* Background shadow card */}
      <div aria-hidden className="absolute inset-0 rounded-lg translate-x-2 translate-y-2" style={{
      backgroundColor: CREAM_DEEP,
      transform: "rotate(1.2deg)"
    }} />

      {/* Main receipt */}
      <div className="relative rounded-lg border p-6 md:p-8 overflow-hidden" style={{
      backgroundColor: PAPER,
      borderColor: `${NAVY}1A`,
      boxShadow: "0 30px 60px -20px rgba(8,20,56,0.18), 0 8px 24px -12px rgba(8,20,56,0.12)",
      fontFamily: FONT_MONO
    }}>
        {/* Subtle paper texture */}
        <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-multiply" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
      }} />

        {/* QUOTE LOCKED stamp */}
        <div aria-hidden className="absolute top-6 right-6 select-none pointer-events-none" style={{
        transform: "rotate(8deg)"
      }}>
          <div className="border-[2.5px] rounded-md px-3 py-2 text-center" style={{
          borderColor: GOLD_DARK,
          opacity: 0.85
        }}>
            <div className="font-extrabold leading-tight" style={{
            fontFamily: FONT_HEAD,
            color: GOLD_DARK,
            fontSize: "0.75rem",
            letterSpacing: "0.15em"
          }}>
              QUOTE
            </div>
            <div className="font-extrabold leading-tight" style={{
            fontFamily: FONT_HEAD,
            color: GOLD_DARK,
            fontSize: "0.95rem",
            letterSpacing: "0.12em"
          }}>
              LOCKED
            </div>
            <div className="text-[8px] font-bold mt-0.5" style={{
            color: GOLD_DARK,
            letterSpacing: "0.15em"
          }}>
              ★ ★ ★
            </div>
          </div>
        </div>

        {/* Letterhead */}
        <div className="relative mb-6">
          <div className="text-[10px] font-bold tracking-[0.28em] uppercase mb-1" style={{
          color: GOLD_DARK,
          fontFamily: FONT_HEAD
        }}>
            Gold Garage Door
          </div>
          <div className="font-extrabold leading-tight" style={{
          fontFamily: FONT_HEAD,
          color: NAVY,
          fontSize: "1.375rem",
          letterSpacing: "-0.015em"
        }}>
            Fixed-Price Repair Quote
          </div>
        </div>

        {/* Meta row */}
        <div className="grid grid-cols-3 gap-3 pb-4 mb-4 border-b border-dashed" style={{
        borderColor: `${NAVY}26`
      }}>
          {[{
          label: "QUOTE #",
          value: "2026-0142"
        }, {
          label: "DATE",
          value: "14 Jun 2026"
        }, {
          label: "SUBURB",
          value: "Bondi NSW"
        }].map(item => <div key={item.label}>
              <div className="text-[9px] font-bold tracking-[0.18em] uppercase mb-0.5" style={{
            color: INK_MUTED,
            fontFamily: FONT_HEAD
          }}>
                {item.label}
              </div>
              <div className="text-[12px] font-bold" style={{
            color: NAVY,
            letterSpacing: "0.02em"
          }}>
                {item.value}
              </div>
            </div>)}
        </div>

        {/* Scope header */}
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED,
          fontFamily: FONT_HEAD
        }}>
            Work scope
          </span>
          <span className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED,
          fontFamily: FONT_HEAD
        }}>
            Status
          </span>
        </div>

        {/* Line items */}
        <div className="space-y-2 mb-5">
          {RECEIPT_LINES.map(line => <ReceiptLine key={line.label} label={line.label} value={line.value} highlight={false} />)}
        </div>

        {/* Divider */}
        <div className="h-px mb-5" style={{
        background: `repeating-linear-gradient(90deg, ${NAVY}40 0, ${NAVY}40 4px, transparent 4px, transparent 8px)`
      }} />

        {/* Fees section */}
        <div className="space-y-2 mb-5">
          {RECEIPT_FEES.map(fee => <ReceiptLine key={fee.label} label={fee.label} value={fee.value} highlight={fee.value === "$0.00" || fee.value === "NEVER"} />)}
        </div>

        {/* Total box */}
        <div className="rounded-md border-2 border-dashed p-4 flex items-center justify-between" style={{
        borderColor: GOLD_DARK,
        backgroundColor: `${GOLD}0D`
      }}>
          <div>
            <div className="text-[9px] font-bold tracking-[0.22em] uppercase mb-0.5" style={{
            color: GOLD_DARK,
            fontFamily: FONT_HEAD
          }}>
              Total (locked)
            </div>
            <div className="text-[11px] font-bold" style={{
            color: NAVY
          }}>
              Quoted on-site
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase mb-0.5" style={{
            color: GOLD_DARK,
            fontFamily: FONT_HEAD
          }}>
              Final
            </div>
            <div className="text-[14px] font-extrabold" style={{
            color: NAVY,
            fontFamily: FONT_HEAD
          }}>
              = QUOTED
            </div>
          </div>
        </div>

        {/* Footer fine print */}
        <div className="mt-5 pt-4 border-t border-dashed text-center" style={{
        borderColor: `${NAVY}26`
      }}>
          <p className="text-[10px] font-medium leading-snug" style={{
          color: INK_MUTED,
          fontFamily: FONT_MONO
        }}>
            Same price even if the job runs longer ·
            <br className="md:hidden" />
            {" "}Workmanship warranty included ·
            <br />
            Customer approves before any tool comes out.
          </p>
        </div>
      </div>
    </div>;
}
function ReceiptLine({
  label,
  value,
  highlight
}: {
  label: string;
  value: string;
  highlight: boolean;
}) {
  return <div className="flex items-center justify-between gap-2">
      <span className="text-[12px] md:text-[13px] truncate" style={{
      color: INK,
      fontFamily: FONT_MONO
    }}>
        {label}
      </span>
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] md:text-[12px] font-bold tracking-wide" style={{
      color: highlight ? GOLD_DARK : NAVY,
      fontFamily: FONT_MONO
    }}>
        {value === "$0.00" && <CheckCircle2 className="h-3 w-3" strokeWidth={2.5} style={{
        color: GOLD_DARK
      } as React.CSSProperties} />}
        {value}
      </span>
    </div>;
}